import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_11: GgxTourDefinition = {
    key: '[RISK-11] Monitoring risks post-production',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 9,
    prerequisite: {
        target: '[test-id="home-page-card-Monitor & Track"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Six thousand conversations a month. Now what?',
        subtitle:
            'Before launch you test a pipeline on a few hundred hand-picked cases. After launch ' +
            'it answers thousands of real customers, every day, and nobody can read them all. ' +
            'Monitoring at that volume is a different job: you sample what people look at, you ' +
            'let a model pre-screen, you compute the same metrics every month, and you watch ' +
            'the trend against thresholds instead of judging one reply at a time. This tour ' +
            'walks through how the credit card intent classifier is monitored in GGX: the queue ' +
            'that collects and labels production data, the judge that pre-screens it, and the ' +
            'monitoring task that turns it into a monthly trend.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen how monitoring works when the volume is too large to read: a queue ' +
            'that collects three months of production questions and labels a sample of them, a ' +
            'judge switch that lets a model take the first pass, a second queue that pulls live ' +
            'traces with guardrail, latency and cost columns on a schedule, and a monitoring ' +
            'task that scores those labels every month and plots accuracy, precision, recall ' +
            'and F1 against thresholds. The habit to keep is the loop: sample, label, score, ' +
            'plot, then go look at whatever moved. Next, see how the labelling itself is done ' +
            'in Post-Production Human Labeling.',
        learned: [
            'At volume you **sample, label, score and plot** rather than read.',
            'A **judge switch** lets a model take the first pass on labels.',
            'The **monitoring task** plots accuracy and F1 against thresholds.',
        ],
    },
    chapters: [
        {
            name: 'Why volume changes the job',
            blurb: 'Sample, pre-screen, measure the same way, watch the trend',
            icon: 'cap',
            steps: [2],
        },
        {
            name: 'The queue',
            blurb: 'Where production labels come from, and how much is labelled',
            icon: 'list',
            steps: [3, 4, 5, 6, 7, 8, 9, 10],
        },
        {
            name: 'Live traces',
            blurb: 'A queue wired to production, on a schedule',
            icon: 'compass',
            steps: [11, 12, 13],
        },
        {
            name: 'The monitoring task',
            blurb: 'Labels become a trend against thresholds',
            icon: 'pulse',
            steps: [14, 15, 16, 17, 18, 19, 20, 21, 22],
        },
    ],
    steps: [
        {
            title: 'Four habits of high-volume monitoring',
            whatHappens: 'next, we open the module where all of this lives.',
            icon: 'cap',
            description:
                '<ul data-ggx-list>' +
                '<li><strong>Sample.</strong> Pull a slice of production traffic on a schedule ' +
                'instead of everything, random or stratified by intent.</li>' +
                '<li><strong>Pre-screen with a judge.</strong> Let a model grade the easy cases ' +
                'so people spend their time on the ones that need a human.</li>' +
                '<li><strong>Measure the same way every period.</strong> One report, one set of ' +
                'metrics, run monthly, so the numbers are comparable.</li>' +
                '<li><strong>Watch the trend, not the point.</strong> A single bad week is ' +
                'noise; a metric drifting toward its threshold is a signal.</li>' +
                '</ul>' +
                '<aside data-ggx-use="For our chatbot">Thousands of intent classifications a ' +
                'month, a few dozen checked by people, one accuracy number per month on a ' +
                'chart with a threshold line. That is the whole setup you are about to ' +
                'see.</aside>' +
                'Click Continue.',
        },
        {
            target: '[test-id="home-page-card-Monitor & Track"] [test-id="launch-button"]',
            title: 'Everything post-production lives here',
            whatHappens: 'the module opens, with Monitoring and Annotation Queues in its sidebar.',
            icon: 'map',
            description:
                '<strong>Monitor & Track</strong> is where a pipeline goes after it ships: the ' +
                'queues that collect and label live data, and the monitoring tasks that score ' +
                'it over time. Click <strong>Launch</strong> on this card.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Launch',
        },
        {
            target: '[test-id="sidenav-item-tab-Annotation Queues"]',
            scrollIntoView: true,
            title: 'Open Annotation Queues',
            whatHappens: 'the list of queues in this project opens.',
            icon: 'map',
            description:
                'An annotation queue is the inbox for production data: real conversations land ' +
                'here, get sampled, get labelled by people or by a judge, and become the ground ' +
                'truth that monitoring compares against. Click <strong>Annotation ' +
                'Queues</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Annotation Queues',
        },
        {
            target: '[test-id^="grid-row-link-Customer Intent Classification Annotations"]',
            targetText: 'Customer Intent Classification Annotations',
            climbToRow: true,
            scrollIntoView: true,
            waitForPresent: '[test-id^="AnnotationQueue-row-"]',
            title: 'Open the intent classification queue',
            whatHappens: 'the queue opens on its Details tab.',
            icon: 'search',
            description:
                'Three queues sit in this project: one fed from synthetic data, one wired to ' +
                'live traces of the full chatbot, and this one, which holds three months of ' +
                'real banking questions with the intent the pipeline assigned to each, across ' +
                '26 intent categories. Click <strong>Customer Intent Classification ' +
                'Annotations</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Customer Intent Classification Annotations',
        },
        {
            target: '[test-id="annotation-queue-tab-stats"]',
            title: 'How much of it has anyone looked at?',
            whatHappens: 'the Statistics tab opens with four tiles and a chart.',
            icon: 'pulse',
            description:
                'The Details tab describes the rubric and the data files; Post-Production Human ' +
                'Labeling covers those. For monitoring the interesting tab is ' +
                '<strong>Statistics</strong>, which tells you how much of the volume has been ' +
                'labelled and what the labels say. Click <strong>Statistics</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Statistics',
        },
        {
            target: '[test-id="card-Total"]',
            climbTo: '.row',
            frame: true,
            scrollIntoView: true,
            title: 'Labelling less than one percent is normal',
            whatHappens: 'next, the same labels over time.',
            icon: 'grid',
            description:
                'Read the four tiles together:' +
                '<ul data-ggx-list>' +
                '<li><strong>Total</strong>: everything that came in over three months, ' +
                'thousands of records.</li>' +
                '<li><strong>Pending</strong> and <strong>Annotated</strong>: almost all still ' +
                'waiting, a few dozen checked, well under one percent.</li>' +
                "<li><strong>Accuracy</strong>: the pipeline's score on the records people " +
                'have checked. This is the number the monitoring task tracks month after ' +
                'month.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">That is not a backlog to clear. At ' +
                'production volume you never label everything; you label a sample large enough ' +
                'to trust.</aside>' +
                'Click Continue.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="annotation-queue-stats-trend-chart"]',
            frame: true,
            scrollIntoView: true,
            title: 'Correct, incorrect and unknown, by week',
            whatHappens: 'next, the judge that can pre-screen the rest.',
            icon: 'pulse',
            description:
                'This chart stacks the labels over time: how many records were marked ' +
                '<strong>Correct</strong>, how many <strong>Incorrect</strong>, and how many are ' +
                'still <strong>Unknown</strong> because nobody has looked yet. The unknown bars ' +
                'are the volume problem made visible. Above the chart, the breakdown by ' +
                'classified intent shows which intents are getting attention and which are ' +
                'not. Click Continue.',
            side: 'top',
            align: 'center',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="annotation-queue-tab-judge"]',
            title: 'Let a model do the first pass',
            whatHappens: 'the Judge tab opens.',
            icon: 'compass',
            description:
                'Thousands of unknowns will not clear themselves. The <strong>Judge</strong> ' +
                'tab is where you attach an LLM-as-a-Judge to the queue so it grades records ' +
                'automatically and people only review the ones it is unsure about. Click ' +
                '<strong>Judge</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Judge',
        },
        {
            target: '[test-id="annotation-queue-judge-toggle"]',
            climbTo: '[test-id="annotation-queue-judge"]',
            frame: true,
            scrollIntoView: true,
            title: 'Automated judging, one switch',
            whatHappens: 'next, a queue that reads live production traces.',
            icon: 'compass',
            description:
                'Turning on <strong>Enable Automated Judging</strong> lets a judge model score ' +
                'every new record against the same rubric your annotators use, so the Unknown ' +
                'bars shrink on their own and human time goes to disagreements and ' +
                'low-confidence cases. It is off on this demo queue, and we will leave it that ' +
                'way; Using Models as Evaluators covers how a judge is built and how you check ' +
                'it agrees with people before trusting it. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="sidenav-item-tab-Annotation Queues"]',
            scrollIntoView: true,
            title: 'Back to the list',
            whatHappens: 'the queue list opens again.',
            icon: 'map',
            description:
                'The queue you just saw was loaded from monthly files. The next one is wired ' +
                'straight to production. Click <strong>Annotation Queues</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Annotation Queues',
        },
        {
            target: '[test-id^="grid-row-link-Credit Card Intent & Response Pipeline - Ongoing"]',
            targetText: 'Credit Card Intent & Response Pipeline - Ongoing Monitoring Traces',
            climbToRow: true,
            scrollIntoView: true,
            waitForPresent: '[test-id^="AnnotationQueue-row-"]',
            title: 'Open the traces queue',
            whatHappens: 'the queue opens on its Details tab.',
            icon: 'search',
            description:
                '<strong>Credit Card Intent & Response Pipeline - Ongoing Monitoring ' +
                'Traces</strong> is set up for the full chatbot, not just the classifier. Click ' +
                'it to open it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Credit Card Intent & Response Pipeline - Ongoing Monitoring Traces',
        },
        {
            target: '[test-id="annotation-queue-data-lake-connection"]',
            frame: true,
            scrollIntoView: true,
            dockCard: 'below',
            title: 'Pulling traces on a schedule',
            whatHappens: 'next, the monitoring task that turns labels into a trend.',
            icon: 'compass',
            description:
                'Instead of uploading files, this queue is wired to production:' +
                '<ul data-ggx-list>' +
                '<li><strong>Source</strong>: traces from the tracing tool the chatbot logs ' +
                'to. Table Registry, Datadog, Langfuse, S3 and custom code are the built-in ' +
                'options.</li>' +
                '<li><strong>Window and schedule</strong>: a start and end date, fetched on a ' +
                'recurring schedule.</li>' +
                '<li><strong>Sampling</strong>: random or stratified by a column, applied ' +
                'before the data is saved.</li>' +
                '<li><strong>Columns</strong>: <code>blocked_by_guardrail</code>, ' +
                '<code>deepeval_hallucination_score</code>, ' +
                '<code>time_input_guardrail</code>, <code>time_response</code>, ' +
                '<code>total_cost</code>, alongside the message and reply.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">At volume you monitor cost and latency and ' +
                'how often the guardrail fired, not just whether the answer was right. The ' +
                'rubric is Every Turn, so each reply in a conversation gets its own ' +
                'label.</aside>' +
                'Click Continue.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="sidenav-item-tab-Monitoring"]',
            scrollIntoView: true,
            title: 'Open Monitoring',
            whatHappens: 'the Monitoring page opens with the tasks in this project.',
            icon: 'map',
            description:
                'Labels on their own are a pile of rows. A <strong>monitoring task</strong> runs ' +
                'a report over them on a schedule and keeps every result, so you get one point ' +
                'per period and a line you can read. Click <strong>Monitoring</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Monitoring',
        },
        {
            target: '[test-id^="grid-row-link-Customer Intent Classification Monitoring"]',
            targetText: 'Customer Intent Classification Monitoring',
            climbToRow: true,
            scrollIntoView: true,
            waitForPresent: '[test-id^="Monitoring-row-"]',
            title: "Open the classifier's monitoring task",
            whatHappens: 'the task opens on its Details tab.',
            icon: 'search',
            description:
                '<strong>Customer Intent Classification Monitoring</strong> tracks accuracy, ' +
                'precision, recall and F1 for the intent classifier from the validated labels ' +
                'in the queue you just saw. Click it to open it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Customer Intent Classification Monitoring',
        },
        {
            target: '[test-id="pipeline"]',
            climbTo: '.card',
            frame: true,
            scrollIntoView: true,
            dockCard: 'below',
            title: 'What the task needs each month',
            whatHappens: 'next, the trend those inputs produce.',
            icon: 'grid',
            description:
                'The task is pinned to <strong>customer_intent_classification_pipeline v1</strong>, ' +
                'so a new pipeline version gets its own line rather than blending in. Its ' +
                '<strong>Input Columns</strong> are the contract with the data: ' +
                '<code>ground_truth</code>, the human or judge label from the queue, and ' +
                '<code>threshold</code>, the level below which a metric counts as a problem. ' +
                'Anything you feed it, from any source, has to carry those two columns. Click ' +
                'Continue.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Overview"]',
            scrollIntoView: true,
            title: 'See the trend',
            whatHappens: 'the Overview opens on the Graph view.',
            icon: 'pulse',
            description: 'Click <strong>Overview</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Overview',
        },
        {
            target: '[test-id="monitoring-overview-graph"]',
            climbTo: '.card-body',
            frame: true,
            scrollIntoView: true,
            dockCard: 'below',
            title: 'Four metrics, five months, two thresholds',
            whatHappens: 'next, how the next point gets on this chart.',
            icon: 'pulse',
            description:
                'Each dot is one monitoring job, placed on its <strong>submission ' +
                'date</strong>:' +
                '<ul data-ggx-list>' +
                '<li><strong>Solid lines</strong>: Overall Accuracy, Precision, Recall and F1 ' +
                'Score, one point per month.</li>' +
                "<li><strong>Flat lines</strong>: each metric's upper and lower " +
                'thresholds.</li>' +
                '<li><strong>Controls</strong>: the date filter narrows the window, and ' +
                'Calendar View shows the same jobs month by month.</li>' +
                '</ul>' +
                '<aside data-ggx-use="How a risk team reads it">Accuracy sat near 90 in ' +
                'August, dipped in September, recovered in October and dipped again in ' +
                'November. Nothing crossed the lower line, so nothing is on fire, but the same ' +
                'dip twice is worth a look at what changed in that traffic.</aside>' +
                'Click Continue.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="run-button"]',
            title: 'How the next point gets added',
            whatHappens: 'the Run Job page opens. We will only look; nothing is submitted.',
            icon: 'compass',
            description:
                'Every dot on that chart was one run of this task. Click <strong>Run</strong> to ' +
                'see what a run asks for.',
            side: 'left',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Run',
        },
        {
            target: '[test-id="submission-date"]',
            frame: true,
            scrollIntoView: true,
            title: 'Tag the run with the period it measures',
            whatHappens: 'next, making it recur.',
            icon: 'grid',
            description:
                '<strong>Submission Date</strong> is the x-axis of the chart you just saw. It ' +
                'says which period this run represents, which is why the August job can be run ' +
                'in October and still land in August. Without it a job shows in the job list ' +
                'but never on the trend. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="scheduling-accordion"]',
            frame: true,
            scrollIntoView: true,
            title: 'Run it every month without remembering to',
            whatHappens: 'next, where the run gets its data.',
            icon: 'compass',
            description:
                '<strong>Scheduling</strong> sets a start date and time, and ' +
                '<strong>Recurrence</strong> makes it repeat, monthly for a task like this. That ' +
                'is the difference between an evaluation and monitoring: one is something you ' +
                'run, the other is something that runs. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="eval-data-options-item-Use Annotation Queue Data"]',
            frame: true,
            scrollIntoView: true,
            title: 'Close the loop with the queue',
            whatHappens: 'the tour wraps up.',
            icon: 'pulse',
            description:
                'A run can read from a registered table, an upload or generated data, but the ' +
                'option that makes this monitoring rather than testing is <strong>Use ' +
                'Annotation Queue Data</strong>: point the run at the Customer Intent ' +
                'Classification Annotations queue and it scores the pipeline against the ' +
                'labels people and the judge produced this month.' +
                '<ul data-ggx-list>' +
                '<li><strong>Queue</strong> collects and labels.</li>' +
                '<li><strong>Task</strong> scores and plots.</li>' +
                '<li><strong>Chart</strong> tells you where to look next.</li>' +
                '</ul>' +
                'That is the whole loop. Click Continue to finish.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};

/** Every code-owned tour, keyed by `LearningTour.key`. */
